/**
 * React Query hooks for user data.
 *
 * React Query hooks for user data.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "common/queryKeys";
import API from "common/utils/Api";
import { ExternalProvider } from "features/signup/externalProviders";
import { User } from "./userTypes";

class UserApi extends API<User> {
  async removeOauth2Client(id: string, externalProvider: ExternalProvider) {
    const res = await fetch(
      `${this.baseUrl}${
        this.entity
      }/oauth2/${externalProvider.toLowerCase()}/${id}`,
      {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${window.localStorage.getItem("jwt") || ""}`,
          "Content-Type": "application/json",
        },
      }
    );
    return res;
  }

  async changePassword(userId: string, newPassword: string) {
    const user = await this.getOne(userId);
    return this.updateOne({ ...user, password: newPassword });
  }
}

const userApi = new UserApi("users");

/**
 * Fetch a user by ID.
 */
export function useUser(userId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: queryKeys.user.detail(userId ?? ""),
    queryFn: () => userApi.getOne(userId ?? ""),
    enabled: enabled && !!userId,
  });
}

/**
 * Create a new user.
 */
export function useCreateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (user: User) => userApi.createOne(user),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.user.all });
    },
  });
}

/**
 * Change password for a user.
 */
export function useChangePassword() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      userId,
      newPassword,
    }: {
      userId: string;
      newPassword: string;
    }) => userApi.changePassword(userId, newPassword),
    onSuccess: async (_, { userId }) => {
      await queryClient.invalidateQueries({
        queryKey: queryKeys.user.detail(userId),
      });
    },
  });
}

/**
 * Remove an OAuth2 client integration from a user.
 */
export function useRemoveOauth2Client() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      userId,
      externalProvider,
    }: {
      userId: string;
      externalProvider: ExternalProvider;
    }) => userApi.removeOauth2Client(userId, externalProvider),
    onSuccess: async (_, { userId }) => {
      await queryClient.invalidateQueries({
        queryKey: queryKeys.user.detail(userId),
      });
    },
  });
}

/**
 * Fetch user directly (not a hook, for use in services).
 * Named differently from userTypes.fetchUserById to avoid conflict.
 */
export async function fetchUserByIdQuery(userId: string): Promise<User> {
  return userApi.getOne(userId);
}

// Export the API for direct use in initializeApp
export { userApi };
